import type { ReactNode } from "react";
import type { GridColumnCount, GridColumnLayout } from "./types";

import { cn } from "../../utils/classnames";
import { getGridColumnClasses } from "./get-grid-column-classes";

/**
 * Children are list contents for the inner `<ul>`: card elements (often with
 * `role="listitem"`), explicit `<li>` wrappers, fragments, or arrays of either.
 *
 * When `as` is `"div"` the children are arbitrary layout content instead.
 */
type GridProps = {
  children: ReactNode;
  className?: string;
  "aria-label"?: string;
  /**
   * Semantics of the grid element. `"ul"` announces a list inside a labelled
   * region — right for a grid of cards. `"div"` emits the grid element alone,
   * with no landmark and no list semantics, for cases like two images side by
   * side mid-article where both would be noise for screen reader users.
   */
  as?: "ul" | "div";
  columnCount?: GridColumnCount;
  columnLayout?: GridColumnLayout;
  listId?: string;
};

function Grid({
  children,
  className = "",
  "aria-label": ariaLabel,
  as = "ul",
  columnCount,
  columnLayout,
  listId,
}: GridProps) {
  if (!children) {
    return null;
  }

  const gridClassName = cn(
    "grid gap-x-12 gap-y-10 md:gap-y-8",
    getGridColumnClasses({ columnCount, columnLayout }),
  );

  if (as === "div") {
    return (
      <div
        aria-label={ariaLabel}
        className={cn(gridClassName, className)}
        id={listId}
        tabIndex={listId ? -1 : undefined}
      >
        {children}
      </div>
    );
  }

  return (
    <section
      aria-label={ariaLabel ?? "Grid of cards"}
      className={className}
      role="region"
    >
      <ul
        className={gridClassName}
        id={listId}
        // Redundant-looking on a `<ul>`, but load-bearing: Tailwind preflight
        // sets `list-style: none`, which makes Safari and VoiceOver drop list
        // semantics without it.
        role="list"
        tabIndex={listId ? -1 : undefined}
      >
        {children}
      </ul>
    </section>
  );
}

export { Grid };
